/**
 * pagesStorage — localStorage-based хранилище контента правовых/информационных
 * страниц (Политика конфиденциальности, Условия, Поддержка, произвольные FAQ).
 *
 * Структура одной страницы:
 * {
 *   slug:        string,           // уникальный идентификатор (для URL)
 *   title:       string,           // заголовок
 *   lastUpdated: string,           // строка с датой последнего обновления
 *   bodyHtml:    string,           // основной HTML контент
 *   builtIn:     boolean,          // встроенная (privacy/terms/support) или пользовательская
 * }
 *
 * Встроенные страницы хранятся под фиксированными slug'ами и могут быть
 * переопределены администратором. Если override отсутствует — рендерится
 * стандартный (JSX) контент компонента.
 */

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'authhub_custom_pages_v1';

export const BUILTIN_SLUGS = ['privacy', 'terms', 'support'];

export const BUILTIN_META = {
    privacy: { title: 'Политика конфиденциальности', route: '/privacy' },
    terms: { title: 'Условия использования', route: '/terms' },
    support: { title: 'Поддержка и FAQ', route: '/support' },
};

const safeParse = (raw) => {
    if (!raw) return {};
    try {
        const v = JSON.parse(raw);
        return v && typeof v === 'object' ? v : {};
    } catch {
        return {};
    }
};

const readAll = () => {
    if (typeof window === 'undefined') return {};
    return safeParse(window.localStorage.getItem(STORAGE_KEY));
};

const writeAll = (data) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    // Сообщить компонентам, прослушивающим изменения, что данные обновились
    window.dispatchEvent(new CustomEvent('authhub:pages-updated'));
};

export const getAllPages = () => {
    const data = readAll();
    return Object.values(data).sort((a, b) => {
        // Сначала встроенные в фиксированном порядке, затем пользовательские по title
        if (a.builtIn && !b.builtIn) return -1;
        if (!a.builtIn && b.builtIn) return 1;
        if (a.builtIn && b.builtIn) {
            return BUILTIN_SLUGS.indexOf(a.slug) - BUILTIN_SLUGS.indexOf(b.slug);
        }
        return (a.title || '').localeCompare(b.title || '');
    });
};

export const getCustomPages = () =>
    getAllPages().filter((p) => !p.builtIn);

export const getPage = (slug) => {
    const data = readAll();
    return data[slug] || null;
};

export const savePage = (page) => {
    if (!page?.slug) throw new Error('savePage: slug required');
    const data = readAll();
    data[page.slug] = {
        slug: page.slug,
        title: page.title || '',
        lastUpdated: page.lastUpdated || new Date().toLocaleDateString('ru-RU'),
        bodyHtml: page.bodyHtml || '',
        builtIn: !!page.builtIn,
    };
    writeAll(data);
    return data[page.slug];
};

export const deletePage = (slug) => {
    const data = readAll();
    if (data[slug]) {
        delete data[slug];
        writeAll(data);
    }
};

export const slugify = (raw) => {
    if (!raw) return '';
    const map = {
        а:'a',б:'b',в:'v',г:'g',д:'d',е:'e',ё:'e',ж:'zh',з:'z',и:'i',й:'i',
        к:'k',л:'l',м:'m',н:'n',о:'o',п:'p',р:'r',с:'s',т:'t',у:'u',ф:'f',
        х:'h',ц:'c',ч:'ch',ш:'sh',щ:'sch',ъ:'',ы:'y',ь:'',э:'e',ю:'yu',я:'ya',
    };
    return raw
        .toLowerCase()
        .split('')
        .map((ch) => map[ch] ?? ch)
        .join('')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64);
};

/**
 * Подписка на изменения в хранилище страниц (и storage event для синхронизации между вкладками).
 */
export const subscribePages = (callback) => {
    if (typeof window === 'undefined') return () => {};
    const handler = () => callback();
    const storageHandler = (e) => {
        if (e.key === STORAGE_KEY) callback();
    };
    window.addEventListener('authhub:pages-updated', handler);
    window.addEventListener('storage', storageHandler);
    return () => {
        window.removeEventListener('authhub:pages-updated', handler);
        window.removeEventListener('storage', storageHandler);
    };
};

// ---------- React-биндинги через useSyncExternalStore ----------
//
// Используем useSyncExternalStore — официальный React 18+ способ для
// внешних хранилищ: исключает рассинхронизацию при гидратации, корректно
// работает с конкурентным рендером и убирает useState + useEffect-обвязку.

// Кэш ссылок: useSyncExternalStore требует, чтобы getSnapshot возвращал
// стабильное значение, если данные не менялись (иначе бесконечный render).
let _allPagesCache = null;
let _customPagesCache = null;
let _pagesByVersion = 0;

const invalidateCaches = () => {
    _allPagesCache = null;
    _customPagesCache = null;
    _pagesByVersion += 1;
};

// Подписка на event с инвалидацией кэшей перед нотификацией React.
const subscribeWithInvalidate = (callback) => subscribePages(() => {
    invalidateCaches();
    callback();
});

const getAllPagesSnapshot = () => {
    if (_allPagesCache === null) _allPagesCache = getAllPages();
    return _allPagesCache;
};

const getCustomPagesSnapshot = () => {
    if (_customPagesCache === null) _customPagesCache = getCustomPages();
    return _customPagesCache;
};

const getServerSnapshot = () => [];

export const useAllPages = () =>
    useSyncExternalStore(subscribeWithInvalidate, getAllPagesSnapshot, getServerSnapshot);

export const useCustomPages = () =>
    useSyncExternalStore(subscribeWithInvalidate, getCustomPagesSnapshot, getServerSnapshot);

/**
 * Хук возвращает одну страницу по slug. Для стабильности подписки
 * каждый slug получает собственную пару subscribe/getSnapshot.
 */
const _pageSnapshotCache = new Map(); // slug -> { version, value }
export const usePage = (slug) => {
    const getSnapshot = () => {
        const cached = _pageSnapshotCache.get(slug);
        if (cached && cached.version === _pagesByVersion) return cached.value;
        const value = getPage(slug);
        _pageSnapshotCache.set(slug, { version: _pagesByVersion, value });
        return value;
    };
    return useSyncExternalStore(subscribeWithInvalidate, getSnapshot, getServerSnapshot);
};

// Гарантируем инвалидацию при прямой записи (savePage/deletePage уже
// диспетчат событие через writeAll, и subscribeWithInvalidate сбрасывает
// кэш до повторного getSnapshot — этого достаточно).

